import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, orderBy, query, where } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Bell, ExternalLink, FileSpreadsheet, Mail, Plus, X } from "lucide-react";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useCancelNotification } from "../../hooks/useNotificationMutations";
import { useEvent } from "../../layouts/EventLayout";
import { useEventParticipants } from "../../hooks/useEventParticipants";
import { useSnackbar } from "../../context/SnackbarContext";
import { PageHeader } from "../../components/PageHeader";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import { DataTable, type Column } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ExcelNotificationImportDialog } from "./ExcelNotificationImportDialog";
import {
  isSharePointConfigured,
  getNotificationsLogUrl,
} from "../../utils/sharepoint";
import type {
  Audience,
  Notification,
  TimestampLike,
} from "@event-planner/shared";
import {
  formatAudienceLabel,
  type AudienceLabelUser,
} from "@event-planner/shared";

type TabValue = "sent" | "scheduled";

function formatTimestamp(ts: TimestampLike | null, timezone: string): string {
  if (!ts) return "—";
  const date = ts.toDate();
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatTarget(
  n: Notification,
  audienceById: Map<string, Audience>,
  userById: Map<string, AudienceLabelUser>,
): string {
  if (!n.audienceId) return "(no recipients)";
  const audience = audienceById.get(n.audienceId);
  return formatAudienceLabel(audience, audienceById, userById);
}

export function NotificationListPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();
  const { cancel, loading: cancelling } = useCancelNotification();

  const [tab, setTab] = useState<TabValue>("sent");
  const [cancelTarget, setCancelTarget] = useState<Notification | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const notificationsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "notifications"),
            where("eventId", "==", eventId),
            orderBy("createdAt", "desc"),
          )
        : null,
    [eventId],
  );

  const {
    data: notifications,
    loading,
    refetch,
  } = useFirestoreCollectionOnce<Notification>(notificationsQuery);

  // Resolve audienceId → audience doc for the Recipients column. Includes
  // anonymous compositions (name == null && system == false) so the
  // formatter can render their includedAudienceIds inline.
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
          )
        : null,
    [eventId],
  );
  const { data: audiences } =
    useFirestoreCollectionOnce<Audience>(audiencesQuery);
  const { persons: eventMembers } = useEventParticipants(eventId);
  const audienceById = useMemo(
    () => new Map(audiences.map((a) => [a.id, a])),
    [audiences],
  );
  const userById = useMemo<Map<string, AudienceLabelUser>>(
    () =>
      new Map(
        eventMembers.map((p) => [p.id, { id: p.id, displayName: p.displayName }]),
      ),
    [eventMembers],
  );

  const timezone = event?.timezone ?? "UTC";

  const sentRows = useMemo(
    () => notifications.filter((n) => n.status === "sent"),
    [notifications],
  );
  const scheduledRows = useMemo(
    () =>
      notifications.filter(
        (n) => n.status === "scheduled" || n.status === "cancelled",
      ),
    [notifications],
  );

  async function handleCancel() {
    if (!cancelTarget) return;
    try {
      await cancel(cancelTarget.id);
      showSuccess("Notification cancelled");
      setCancelTarget(null);
      refetch();
    } catch {
      showError("Failed to cancel notification");
    }
  }

  const channelChips = (channels: Notification["channels"]) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {channels.includes("push") && (
        <Chip
          icon={<Bell size={14} />}
          label="Push"
          size="small"
          variant="outlined"
        />
      )}
      {channels.includes("email") && (
        <Chip
          icon={<Mail size={14} />}
          label="Email"
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );

  const sentColumns: Column<Notification>[] = [
    {
      id: "title",
      label: "Title",
      render: (n) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {n.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {n.body.length > 60 ? `${n.body.slice(0, 60)}…` : n.body}
          </Typography>
        </Box>
      ),
    },
    {
      id: "channels",
      label: "Channels",
      render: (n) => channelChips(n.channels),
    },
    {
      id: "target",
      label: "Recipients",
      render: (n) => formatTarget(n, audienceById, userById),
    },
    {
      id: "sentAt",
      label: "Sent",
      render: (n) => formatTimestamp(n.sentAt, timezone),
    },
  ];

  const scheduledColumns: Column<Notification>[] = [
    {
      id: "title",
      label: "Title",
      render: (n) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {n.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {n.body.length > 60 ? `${n.body.slice(0, 60)}…` : n.body}
          </Typography>
        </Box>
      ),
    },
    {
      id: "channels",
      label: "Channels",
      render: (n) => channelChips(n.channels),
    },
    {
      id: "target",
      label: "Recipients",
      render: (n) => formatTarget(n, audienceById, userById),
    },
    {
      id: "scheduledAt",
      label: "Scheduled For",
      render: (n) => formatTimestamp(n.scheduledAt, timezone),
    },
    {
      id: "status",
      label: "Status",
      render: (n) => (
        <Chip
          label={n.status}
          size="small"
          color={n.status === "cancelled" ? "default" : "primary"}
          variant={n.status === "cancelled" ? "outlined" : "filled"}
        />
      ),
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (n) =>
        n.status === "scheduled" ? (
          <Tooltip title="Cancel scheduled send">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setCancelTarget(n);
              }}
              aria-label={`Cancel ${n.title}`}
            >
              <X size={18} />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  // Phone: the title shows its message preview over two lines instead of a
  // single unwrapped line; recipients and time sit under it; channel and
  // status chips share one row; cancel moves to the row menu.
  const PHONE_SLOTS: Record<string, "secondary" | "chips" | "hidden"> = {
    channels: "chips",
    target: "secondary",
    sentAt: "secondary",
    scheduledAt: "secondary",
    status: "chips",
    actions: "hidden",
  };
  function toPhoneColumns(cols: Column<Notification>[]): Column<Notification>[] {
    return cols.map((col) =>
      col.id === "title"
        ? {
            ...col,
            render: (n) => (
              <Box>
                <Typography fontWeight={600}>{n.title}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {n.body}
                </Typography>
              </Box>
            ),
          }
        : { ...col, phone: PHONE_SLOTS[col.id] },
    );
  }
  function notificationRowActions(n: Notification): OverflowAction[] {
    return n.status === "scheduled"
      ? [
          {
            label: "Cancel scheduled send",
            icon: <X size={18} />,
            onClick: () => setCancelTarget(n),
            destructive: true,
          },
        ]
      : [];
  }

  const desktopColumns = tab === "sent" ? sentColumns : scheduledColumns;
  const currentColumns = isPhone ? toPhoneColumns(desktopColumns) : desktopColumns;
  const currentRows = tab === "sent" ? sentRows : scheduledRows;
  const emptyMessage =
    tab === "sent"
      ? "No notifications have been sent yet."
      : "No scheduled notifications.";

  return (
    <Box>
      <PageHeader
        title="Notifications"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("new")}
                >
                  Compose
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <FileSpreadsheet size={18} />,
                  onClick: () => setImportOpen(true),
                  testId: "import-notification-button",
                },
                ...(isSharePointConfigured()
                  ? [
                      {
                        label: "Open in SharePoint",
                        icon: <ExternalLink size={18} />,
                        onClick: () =>
                          window.open(getNotificationsLogUrl(event?.name), "_blank"),
                        testId: "open-sharepoint-notifications-button",
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            {isSharePointConfigured() && (
              <Tooltip title="Open this event's notifications log in SharePoint">
                <Button
                  variant="outlined"
                  startIcon={<ExternalLink size={18} />}
                  onClick={() =>
                    window.open(getNotificationsLogUrl(event?.name), "_blank")
                  }
                  data-testid="open-sharepoint-notifications-button"
                >
                  Open in SharePoint
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={() => setImportOpen(true)}
              data-testid="import-notification-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("new")}
            >
              Compose
            </Button>
          </Box>
          )
        }
      />

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value as TabValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={`Sent (${sentRows.length})`} value="sent" />
        <Tab
          label={`Scheduled (${scheduledRows.filter((n) => n.status === "scheduled").length})`}
          value="scheduled"
        />
      </Tabs>

      {!loading && currentRows.length === 0 ? (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      ) : (
        <DataTable
          columns={currentColumns}
          data={currentRows}
          loading={loading}
          ariaLabel={`${tab} notifications table`}
          getRowKey={(n) => n.id}
          onRowClick={(n) => navigate(n.id)}
          phoneRowActions={notificationRowActions}
          phoneRowActionsLabel={(n) => `More actions for ${n.title}`}
        />
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancel}
        title="Cancel Notification"
        message={
          cancelTarget
            ? `Cancel the scheduled send of "${cancelTarget.title}"? Attendees will not receive this notification.`
            : ""
        }
        loading={cancelling}
      />

      {eventId && (
        <ExcelNotificationImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          eventId={eventId}
          timezone={timezone}
          onImported={() => refetch()}
        />
      )}
    </Box>
  );
}
